import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Price } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { MANAGED_WEBSITE, OTHER_MANAGED_SERVICES, dfyBundle, managedService, oneTimeAddOn } from '@gjl/voice/pricing'

export default function DigitalPartnershipPage() {
  const breadcrumbs = generateBreadcrumbs('/services/digital-partnership', breadcrumbLabels)

  const managedWebsite = managedService('managed-website')
  const growthPartner = dfyBundle('growth-partner')
  const concierge = dfyBundle('concierge')
  // The onboarding fee was a "$250" prose literal in three places; read it from
  // the catalogue so a reprice cannot leave this page stale.
  const launchBuild = oneTimeAddOn('launch-build')

  useSEO({
    title: 'Ongoing Support | Year-Round Digital Partnership',
    description:
      'Year-round digital support for music organizations. Website updates, email campaigns, analytics, and strategy aligned with your season.',
    url: '/services/digital-partnership',
    canonical: '/services/digital-partnership',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Year-Round Digital Partnership',
      description:
        'Ongoing digital support for classical music organizations including website updates, email campaigns, and strategic consulting.',
      url: '/services/digital-partnership',
    }),
  })

  // One entrance, on the hero only; the per-section staggered reveals and
  // card hover lifts were the stock kit and are gone.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-20 md:space-y-36 md:px-12 md:py-28">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Ongoing Support for Music Organizations</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your organization doesn&apos;t stop after launching a new website. You need email campaigns during concert
          season. Website updates throughout the year. Analytics to track ticket sales. SEO improvements for next
          season.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Our managed services give you ongoing support tailored to your organization&apos;s rhythm, whether you&apos;re
          running a festival, orchestra, opera company, or choral organization. Focus on creating transformative musical
          experiences while we handle your digital strategy.
        </p>
        <p className="text-fluid-body text-foreground mx-auto max-w-2xl font-medium">
          Think of us as an extension of your team, someone who understands both the music and the tech.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
        </div>
      </section>

      {/* Problem Section. A typographic grid; the names carry it. */}
      <section aria-labelledby="why-year-round" className="space-y-12">
        <h2 id="why-year-round" className="text-fluid-h3 max-w-3xl">
          Why Music Organizations Need Year-Round Digital Support
        </h2>
        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <div key={item.problem} className="space-y-2">
              <h3 className="text-fluid-h4">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[52ch] leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
        <p className="text-muted-foreground max-w-2xl italic">
          A one-time website build isn&apos;t enough. Organizations need ongoing digital support aligned with their
          season and operational cycle.
        </p>
      </section>

      {/* Managed Services Ladder. These three ARE side-by-side comparable plans
          (each includes the one before it), so they keep a box: one plain card
          ground, no border or lift, and the recommended tier set one step up
          the surface ramp rather than given a glow. */}
      <section aria-labelledby="pick-level" className="space-y-12">
        <div className="grid gap-6 md:grid-cols-12">
          <h2 id="pick-level" className="text-fluid-h3 md:col-span-5">
            Pick Your Level of Support
          </h2>
          <p className="text-muted-foreground text-fluid-body max-w-[60ch] md:col-span-7">
            Every managed service includes the underlying software. Start with your website, add email when you are
            ready, or hand us the whole digital operation.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {ladderTiers(managedWebsite, growthPartner, concierge).map((tier) => (
            <div
              key={tier.name}
              className={`space-y-5 rounded-md p-6 md:p-8 ${tier.recommended ? 'bg-surface-raised' : 'bg-card'}`}
            >
              <div className="space-y-2">
                {tier.recommended && <p className="text-primary text-sm font-medium">Most popular</p>}
                <h3 className="text-2xl">{tier.name}</h3>
                <Price size="lg">{tier.price}</Price>
                <p className="text-muted-foreground text-sm leading-relaxed">{tier.note}</p>
              </div>
              <ul className="text-muted-foreground space-y-1.5 text-sm">
                {tier.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="max-w-3xl space-y-3">
          <p className="text-muted-foreground text-sm leading-relaxed">
            Every managed service is also available on its own:{' '}
            {[
              {
                id: MANAGED_WEBSITE.id,
                name: MANAGED_WEBSITE.name,
                priceLabel: `from $${MANAGED_WEBSITE.tiers[0].priceMonthly}/mo`,
              },
              ...OTHER_MANAGED_SERVICES.map((s) => ({
                id: s.id,
                name: s.name,
                priceLabel: `$${s.priceMonthly}/mo`,
              })),
            ].map((service, idx, arr) => (
              <span key={service.id}>
                <Link
                  to={`/services/done-for-you#${service.id}`}
                  className="text-primary focus-visible:outline-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
                >
                  {service.name}
                </Link>{' '}
                ({service.priceLabel}){idx < arr.length - 1 ? ', ' : '. '}
              </span>
            ))}
            See the full menu on our{' '}
            <Link
              to="/services/done-for-you"
              className="text-primary focus-visible:outline-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              Done-For-You Services
            </Link>{' '}
            page.
          </p>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Every plan starts with a one-time onboarding (the {launchBuild.price} Launch build counts as it) and a
            3-month minimum term, then runs month to month. Leaving is always free, and you take a full export of your
            data with you.
          </p>
        </div>
      </section>

      {/* What's Included Across All Tiers. Asymmetric 5/7: heading left, the
          six inclusions as a two-column list on the right. */}
      <section aria-labelledby="included" className="grid gap-10 md:grid-cols-12 md:gap-8">
        <div className="space-y-4 md:col-span-5">
          <h2 id="included" className="text-fluid-h3">
            What&apos;s Included Across All Plans
          </h2>
          <p className="text-muted-foreground">Core services that every managed plan includes.</p>
        </div>
        <div className="grid gap-x-10 gap-y-10 sm:grid-cols-2 md:col-span-7">
          {INCLUDED.map((item) => (
            <div key={item.title} className="space-y-2">
              <h3 className="text-xl">{item.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works. The months already carry the order, so each row is the
          period on the left and what happens on the right, separated by space. */}
      <section aria-labelledby="example-year" className="space-y-12">
        <h2 id="example-year" className="text-fluid-h3 max-w-3xl">
          Example: How a Partnership Works for a Concert Organization
        </h2>
        <ol className="space-y-10">
          {TIMELINE.map((item) => (
            <li key={item.month} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl md:col-span-5">{item.month}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.description}</p>
            </li>
          ))}
        </ol>
        <p className="text-muted-foreground italic">
          Your plan adapts to your organization&apos;s seasonal rhythm, not a generic monthly checklist.
        </p>
      </section>

      {/* Benefits Section */}
      <section aria-labelledby="why-partnership" className="space-y-12">
        <h2 id="why-partnership" className="text-fluid-h3 max-w-3xl">
          Why Organizations Choose Partnership Support
        </h2>
        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
          {BENEFITS.map((item) => (
            <div key={item.benefit} className="space-y-2">
              <h3 className="text-xl">{item.benefit}</h3>
              <p className="text-muted-foreground max-w-[52ch] leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section aria-labelledby="faq" className="space-y-12">
        <h2 id="faq" className="text-fluid-h3">
          Frequently Asked Questions
        </h2>
        <div className="space-y-10">
          {[
            {
              question: 'Can we start small and add services later?',
              answer: `Yes. Many organizations start with Managed Website and add Managed Email later, which becomes the Growth Partner bundle at ${growthPartner.price}. You can adjust your plan anytime.`,
            },
            {
              question: 'What are the terms?',
              answer: `Every done-for-you plan starts with a one-time onboarding (the ${launchBuild.price} Launch build counts as it) and a 3-month minimum term. After that you are month to month. Leaving is always free, and you get a full export of your data.`,
            },
            {
              question: 'Do we need to have our website built by you first?',
              answer: `Not necessarily. We can manage websites built elsewhere after a one-time onboarding. If we build your site, the ${launchBuild.price} Launch build counts as your onboarding.`,
            },
            {
              question: 'How do email campaigns work?',
              answer:
                "We write, design, and schedule email campaigns aligned with your organization's calendar. You review and approve before sending. We track open rates, click rates, and adjust strategy based on data.",
            },
            {
              question: 'Can we request additional features or projects during the plan?',
              answer:
                'Yes. On Managed Website, update requests are unlimited, one active at a time with a 72-hour turnaround. Larger projects (major redesigns, complex integrations) are scoped and quoted up front.',
            },
            {
              question: 'How is this different from hiring a full-time marketing person?',
              answer:
                "You get specialized classical music + digital marketing expertise for a fraction of the cost of a full-time hire. Plus, we bring technical skills (development, integrations, analytics) that most marketing staff don't have.",
            },
          ].map((item) => (
            <div key={item.question} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl md:col-span-5">{item.question}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section. Unboxed. */}
      <section aria-labelledby="cta" className="mx-auto max-w-3xl space-y-6 text-center">
        <h2 id="cta" className="text-fluid-h2">
          Ready to Grow Your Digital Presence?
        </h2>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Schedule a free 15-minute strategy call to discuss your organization&apos;s needs and find the right plan for
          your goals.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/contact">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Or Send Us a Message
              </Button>
            </m.div>
          </Link>
        </div>
        <p className="text-muted-foreground text-sm">
          No sales pitch. Just an honest conversation about how ongoing support can help your organization grow.
        </p>
      </section>
    </div>
  )
}

/** The three-plan ladder. Prices and notes come from the catalogue accessors
 * passed in, never literals. */
function ladderTiers(
  managedWebsite: ReturnType<typeof managedService>,
  growthPartner: ReturnType<typeof dfyBundle>,
  concierge: ReturnType<typeof dfyBundle>
) {
  return [
    {
      name: managedWebsite.name,
      price: `$${managedWebsite.priceMonthly}/month`,
      note: 'Your website, kept current for you. The website software is included.',
      recommended: false,
      features: [
        'Unlimited update requests, one active at a time',
        '72-hour turnaround on every request',
        'Hosting, editor, custom domain, forms & SEO included',
        'Technical maintenance & bug fixes',
        'Analytics reporting',
      ],
    },
    {
      name: growthPartner.name,
      price: growthPartner.price,
      note: growthPartner.note,
      recommended: true,
      features: [
        'Everything in Managed Website',
        'We plan, write, design & send your email',
        'About 2 campaigns per month plus 1 automation',
        'Email software included',
        'Campaigns aligned with your season calendar',
      ],
    },
    {
      name: concierge.name,
      price: concierge.price,
      note: concierge.note,
      recommended: false,
      features: [
        'Everything in Growth Partner',
        'Fundraising campaigns run for you',
        'Box office setup, on-sales & reporting handled',
        'Ongoing strategy & prioritization',
        'All underlying software included',
      ],
    },
  ]
}

const PROBLEMS = [
  {
    problem: 'Seasonal Cycles & Evolving Needs',
    description:
      'Your digital needs change throughout the year. Concert season requires active email marketing. Summer needs website updates and season planning. You need someone who understands this rhythm.',
  },
  {
    problem: 'Ongoing Website Updates',
    description:
      'Concert dates change. Artists get added. Programs evolve. You need someone who can update your website quickly without waiting weeks for a developer to respond.',
  },
  {
    problem: 'Email Campaigns for Audience Engagement',
    description:
      "Season announcements. Ticket launch emails. Concert reminders. Donor campaigns. These don't write and send themselves. You need consistent campaign management.",
  },
  {
    problem: 'No Time for SEO or Analytics',
    description:
      'You know you should be tracking website traffic, ticket sales sources, and audience metrics. But who has time? You need someone monitoring this continuously.',
  },
  {
    problem: 'Technical Issues & Bug Fixes',
    description:
      "Forms break. Integrations stop working. Emails bounce. When tech issues happen during ticket launch week, you can't wait days for a fix. You need immediate support.",
  },
  {
    problem: 'No Strategic Partner',
    description:
      "You need someone who understands your organization's goals, tracks progress year over year, and proactively suggests improvements. Not just a vendor who builds once and disappears.",
  },
]

const INCLUDED = [
  {
    title: 'Season-Aligned Planning',
    description:
      "We align digital strategy with your organization's calendar, from season planning to concert launches to summer programming.",
  },
  {
    title: 'Ongoing Website Updates',
    description:
      'Concert announcements, artist bios, program changes, schedule adjustments, and content refreshes handled promptly.',
  },
  {
    title: 'Technical Maintenance',
    description: 'Hosting, security updates, backups, SSL management, and performance monitoring, all managed for you.',
  },
  {
    title: 'Analytics & Reporting',
    description:
      "Track website traffic, ticket sales funnels, and audience engagement data to understand what's working.",
  },
  {
    title: 'Strategic Guidance',
    description:
      "Regular consultation on digital strategy, audience growth, and tech improvements tailored to your organization's goals.",
  },
  {
    title: 'Direct Access',
    description:
      'No ticketing systems or endless back-and-forth. Email, call, or Slack directly with someone who knows your organization.',
  },
]

const TIMELINE = [
  {
    month: 'January-March: Planning Season',
    description:
      "We help plan your next season's digital strategy, update website content, and prepare email campaigns for season ticket sales launch.",
  },
  {
    month: 'April-May: Season Launch',
    description:
      'Email campaigns to announce new season and drive early ticket sales, SEO improvements to rank for concert searches, website updates with new repertoire and artists.',
  },
  {
    month: 'June-August: Summer Season',
    description:
      'Ongoing concert promotion emails, ticket sales tracking and optimization, quick updates for programming changes, technical support for any issues.',
  },
  {
    month: 'September-November: Fall Season',
    description:
      'Continue audience engagement campaigns, optimize donation appeals, update website with new programs and artist info.',
  },
  {
    month: 'December: Year-End & Strategic Planning',
    description:
      'Holiday concert promotions, year-end giving campaigns, review annual data, set digital goals for next season.',
  },
]

const BENEFITS = [
  {
    benefit: 'Predictable Monthly Investment',
    description:
      'No surprise invoices. One monthly price you can plan around. After the 3-month minimum, you are month to month.',
  },
  {
    benefit: 'Continuous Improvement',
    description:
      'We track metrics year over year and continuously optimize your website, email campaigns, and audience engagement strategies.',
  },
  {
    benefit: 'Faster Response Times',
    description:
      'Managed clients get priority support. Urgent bug fixes come first, and update requests turn around within 72 hours. No waiting weeks for availability.',
  },
  {
    benefit: 'Proactive Strategy',
    description:
      "We don't wait for you to ask. We proactively suggest improvements, identify opportunities, and keep you ahead of the curve.",
  },
  {
    benefit: 'Deep Organizational Knowledge',
    description:
      'After a few months, we know your programs, your audience, your goals. We become true partners, not just vendors.',
  },
  {
    benefit: 'Audience Growth Over Time',
    description:
      'Small optimizations compound. Email campaigns improve. SEO rankings increase. Ticket conversion rates rise. Audiences grow year over year.',
  },
]
